import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import { If, Then, Else } from 'react-if';
import { hot } from 'react-hot-loader';
import { compose } from 'recompose';

import * as ROUTES from '../../constants/routes';

import { withAuthentication } from '../../session/index';
import { NotificationContainer } from 'react-notifications';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-notifications/lib/notifications.css';

import Header from '../../components/layout/MainNavbar/MainNavbar';
import Posts from '../Posts';
import UpdatePost from '../UpdatePost/index';
import CreatePost from '../CreatePost/index';
import Policy from '../Policy/index';
import Landing from '../Landing/index';
import AccountSettings from '../Account/index';
import Preview from '../Preview/index';
import StripeSuccessRedirect from '../StripeSuccessRedirect';
import PostsPreview from '../Posts/PostsPreview';
import MainLayout from '../../components/layout/Main';
import classNames from 'classnames';
import { POST_PAGE_TYPES } from '../../constants/postPageTypes';
import { PendingPostIcon } from '../../components/elements/icons/Icons';
import { ReactComponent as InvitationPostsIcon } from './invitations-posts-icon.svg';
import { ReactComponent as YourPostsIcon } from './your-posts-icon.svg';
import NotificationBadge from '../../components/elements/NotificationBadge/NotificationBadge';
import { When } from 'react-if';
import { useAuth } from '../../hooks/useAuth';
import {
    withNotificationProvider,
    useNotificationContext,
} from '../../providers/NotificationProvider';
import { usePostContext, withPostProvider } from '../../providers/PostProvider';
import UnauthorizedLayout from '../../components/layout/Unauthorized';
import SignInPage from '../Signin';
import SignUpPage from '../Signup';
import ForgotPassword from '../ForgotPassword';
import { IS_SIDEKICK_APP } from '../../constants/projects';

const App = () => {
    const { isAdmin } = useAuth();

    const { invitationsPostCount, unreadPostCount, pendingPostsCount } = useNotificationContext();
    const { currentPostPage, setCurrentPostPage } = usePostContext();

    const handleMenuItemClick = useCallback(
        newPageType => () => {
            setCurrentPostPage(newPageType);
        },
        [setCurrentPostPage]
    );

    const menuRenderer = useCallback(
        () => (
            <>
                <p className="main-layout__nav-link nav-headline">POSTS</p>
                <div
                    className={classNames('btn btn-link main-layout__nav-link mb-2', {
                        'main-layout__nav-link--active':
                            currentPostPage === POST_PAGE_TYPES.INVITATIONS,
                    })}
                    onClick={handleMenuItemClick(POST_PAGE_TYPES.INVITATIONS)}
                >
                    <div className="main-layout__nav-icon">
                        <InvitationPostsIcon />
                    </div>
                    <p>Invitations</p>
                    <NotificationBadge unreadCount={invitationsPostCount} />
                </div>
                <div
                    className={classNames('btn btn-link main-layout__nav-link mb-2', {
                        'main-layout__nav-link--active':
                            currentPostPage === POST_PAGE_TYPES.MY_POSTS,
                    })}
                    onClick={handleMenuItemClick(POST_PAGE_TYPES.MY_POSTS)}
                >
                    <div className="main-layout__nav-icon">
                        <YourPostsIcon />
                    </div>
                    <If condition={IS_SIDEKICK_APP}>
                        <Then>
                            <p>My Projects</p>
                        </Then>
                        <Else>
                            <p>My Posts</p>
                        </Else>
                    </If>
                    <NotificationBadge unreadCount={unreadPostCount} />
                </div>
                <When condition={isAdmin}>
                    <div
                        className={classNames('btn btn-link main-layout__nav-link mb-2', {
                            'main-layout__nav-link--active':
                                currentPostPage === POST_PAGE_TYPES.PENDING_POSTS,
                        })}
                        onClick={handleMenuItemClick(POST_PAGE_TYPES.PENDING_POSTS)}
                    >
                        <div className="main-layout__nav-icon">
                            <PendingPostIcon />
                        </div>
                        <p>Pending Posts</p>
                        <NotificationBadge unreadCount={pendingPostsCount} />
                    </div>
                </When>
            </>
        ),
        [
            pendingPostsCount,
            currentPostPage,
            unreadPostCount,
            invitationsPostCount,
            isAdmin,
            handleMenuItemClick,
        ]
    );

    return (
        <div>
            <Header />
            <Switch>
                <Route exact path={ROUTES.LANDING} component={Landing} />
                <Route exact path={ROUTES.PREVIEW} component={PostsPreview} />
                <Route exact path={ROUTES.ACCOUNT_SETTINGS} component={AccountSettings} />
                <Route exact path={ROUTES.POLICY} component={Policy} />
                <Route
                    exact
                    path={[
                        ROUTES.CREATE_POST,
                        ROUTES.UPDATE_POST,
                        ROUTES.POSTS,
                        ROUTES.PROFILE,
                        ROUTES.MY_ALL_POSTS,
                    ]}
                >
                    <MainLayout menuRenderer={menuRenderer}>
                        <Switch>
                            <Route exact path={ROUTES.CREATE_POST} component={CreatePost} />
                            <Route exact path={ROUTES.UPDATE_POST} component={UpdatePost} />
                            <Route path={[ROUTES.POSTS, ROUTES.MY_ALL_POSTS]} component={Posts} />
                            <Route path={ROUTES.PROFILE} component={Posts} />
                        </Switch>
                    </MainLayout>
                </Route>
                <Route export path={[ROUTES.SIGN_IN, ROUTES.SIGN_UP, ROUTES.RESET_PASSWORD]}>
                    <UnauthorizedLayout>
                        <Switch>
                            <Route exact path={ROUTES.SIGN_UP} component={SignUpPage} />
                            <Route exact path={ROUTES.SIGN_IN} component={SignInPage} />
                            <Route exact path={ROUTES.RESET_PASSWORD} component={ForgotPassword} />
                        </Switch>
                    </UnauthorizedLayout>
                </Route>
                <Route path={ROUTES.STRIPE_SUCCESS_AUTH} component={StripeSuccessRedirect} />
                <Route component={Landing} />
            </Switch>
            <NotificationContainer />
        </div>
    );
};

const enhance = compose(withAuthentication, withNotificationProvider, withPostProvider);

export default hot(module)(enhance(App));
